import { forwardRef } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
  useMonkeysLocaleMessages,
  useMonkeysStatusStates,
} from '../../provider/context';
import { BaseSkeleton } from './BaseSkeleton';
import { SkeletonScope } from '../skeleton/motion';
import type { BaseLoadingStateProps } from './types';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

function DefaultLoadingIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={className}
      fill="none"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        className="opacity-25"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M12 3a9 9 0 0 1 9 9h-3a6 6 0 0 0-6-6V3Z"
      />
    </svg>
  );
}

export const BaseLoadingState = forwardRef<
  HTMLDivElement,
  BaseLoadingStateProps
>(function BaseLoadingState(
  {
    icon,
    title,
    description,
    actions,
    variant,
    animation,
    appearance,
    className,
    classNames,
    style,
    role = 'status',
    'aria-live': ariaLive = 'polite',
    'aria-busy': ariaBusy = true,
    'aria-label': nativeAriaLabel,
    'aria-labelledby': nativeAriaLabelledBy,
    ...stateProps
  },
  ref,
) {
  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const { loading } = useMonkeysLocaleMessages();
  const statusStates = useMonkeysStatusStates();
  const resolvedVariant = variant ?? statusStates.loading?.variant ?? 'spinner';
  const densityClassName =
    statusStates.density === 'compact'
      ? 'min-h-0 px-3 py-3'
      : statusStates.density === 'comfortable'
        ? 'min-h-56 px-8 py-12'
        : undefined;
  const theme = resolveBaseAppearance(resolvedAppearance);
  const resolvedIcon =
    resolvedVariant === 'skeleton' && icon === undefined
      ? undefined
      : icon === undefined
        ? <DefaultLoadingIcon className={theme.slots.loadingSpinner} />
        : icon;
  const resolvedAriaLabel =
    nativeAriaLabel ??
    (nativeAriaLabelledBy === undefined
      ? typeof title === 'string'
        ? title
        : loading.spinner
      : undefined);

  return (
    <div
      {...componentAttributes}
      {...stateProps}
      ref={ref}
      role={role}
      aria-live={ariaLive}
      aria-busy={ariaBusy}
      aria-label={resolvedAriaLabel}
      aria-labelledby={nativeAriaLabelledBy}
      data-monkeys-component="loading-state"
      data-slot="root"
      data-state="loading"
      data-variant={resolvedVariant}
      data-density={statusStates.density}
      className={cn(theme.slots.stateRoot, densityClassName, classNames?.root, className)}
      style={{
        ...theme.styles.stateRoot,
        ...style,
      }}
    >
      {resolvedVariant === 'skeleton' ? (
        <SkeletonScope animation={animation}>
          <div className="flex w-full max-w-sm flex-col gap-3" data-slot="skeleton">
            <BaseSkeleton variant="block" className="h-20" />
            <BaseSkeleton variant="line" className="w-4/5" />
            <BaseSkeleton variant="line" className="w-3/5" />
          </div>
        </SkeletonScope>
      ) : hasRenderableNode(resolvedIcon) ? (
        <div
          data-slot="icon"
          data-state="loading"
          className={cn(theme.slots.stateIcon, classNames?.icon)}
          style={theme.styles.stateIcon}
        >
          {resolvedIcon}
        </div>
      ) : null}
      {resolvedVariant !== 'skeleton' && hasRenderableNode(title) ? (
        <div
          data-slot="title"
          data-state="loading"
          className={cn(theme.slots.stateTitle, classNames?.title)}
        >
          {title}
        </div>
      ) : null}
      {resolvedVariant !== 'skeleton' && hasRenderableNode(description) ? (
        <div
          data-slot="description"
          data-state="loading"
          className={cn(theme.slots.stateDescription, classNames?.description)}
        >
          {description}
        </div>
      ) : null}
      {hasRenderableNode(actions) ? (
        <div
          data-slot="actions"
          data-state="loading"
          className={cn(theme.slots.stateActions, classNames?.actions)}
        >
          {actions}
        </div>
      ) : null}
    </div>
  );
});
